import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, DownloadCloud, PauseCircle, Save, UploadCloud } from "lucide-react";
import {
  adminDesktopUpdateReleaseUploadSchema,
  adminDesktopUpdateReleaseSchema,
  inferDesktopUpdateVersionFromFileName,
  type AdminDesktopUpdateReleaseInput,
  type AdminDesktopUpdateReleaseUploadInput,
  type AdminDesktopUpdateReleaseView
} from "@shudian/contracts";
import {
  adminDisableDesktopUpdateRelease,
  adminEnableDesktopUpdateRelease,
  adminListDesktopUpdateReleases,
  adminPublishDesktopUpdateRelease,
  adminUploadDesktopUpdateRelease,
  adminUpdateDesktopUpdateRelease
} from "../api/admin.js";
import { Alert } from "../components/ui/Alert.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from "../components/ui/Table.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime } from "../lib/format.js";

type ReleaseFormValues = AdminDesktopUpdateReleaseInput;
type PublishMode = "upload" | "external";
type SelectOption = { value: string; label: string };

const UPDATE_TARGET_OPTIONS: SelectOption[] = [{ value: "windows", label: "Windows" }];
const UPDATE_ARCH_OPTIONS: SelectOption[] = [{ value: "x86_64", label: "64 位" }];
const UPDATE_CHANNEL_OPTIONS: SelectOption[] = [{ value: "stable", label: "正式通道" }];

export function DesktopUpdatesPage() {
  const queryClient = useQueryClient();
  const [selectedRelease, setSelectedRelease] = useState<AdminDesktopUpdateReleaseView | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "desktop-updates"],
    queryFn: () => adminListDesktopUpdateReleases({ includeDisabled: true, limit: 100 })
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "desktop-updates"] });
    await queryClient.invalidateQueries({ queryKey: ["admin", "audit"] });
  };

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    return (
      <Alert tone="danger">
        {error instanceof CloudApiError ? describeError(error.code) : "加载失败"}
      </Alert>
    );
  }

  const releases = data?.releases ?? [];
  const enabledRelease = releases.find((release) => release.enabled);
  const disabledCount = releases.filter((release) => !release.enabled).length;

  return (
    <div>
      <PageHeader
        eyebrow="发布与采集 / 桌面更新"
        title="桌面更新"
        description="管理桌面端更新发布记录。签名来自打包产物，后台只负责保存和分发更新元数据。"
        actions={
          <Button variant="secondary" onClick={() => void refresh()}>
            <DownloadCloud className="h-4 w-4" />
            刷新发布记录
          </Button>
        }
      />

      <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard
          label="当前启用版本"
          value={enabledRelease?.version ?? "未启用"}
          detail={enabledRelease ? `${enabledRelease.target} / ${enabledRelease.arch} / ${enabledRelease.channel}` : "桌面端不会拉取更新"}
          tone={enabledRelease ? "success" : "warning"}
          icon={<DownloadCloud className="h-4 w-4" />}
        />
        <MetricCard label="发布记录" value={releases.length} detail={`${disabledCount} 条停用记录`} tone="info" />
        <MetricCard label="最近发布时间" value={formatDateTime(enabledRelease?.pubDate ?? enabledRelease?.createdAt)} detail="当前启用版本" tone="neutral" />
      </section>

      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
      {successMessage && <Alert tone="success">{successMessage}</Alert>}

      <SectionSurface className="mb-5">
        <SectionHeader
          title={selectedRelease ? "编辑桌面更新" : "发布桌面更新"}
          description="发布记录会供桌面端更新器拉取；签名来自打包产物，不在后台生成。"
        />
        <div className="px-4 py-4">
        <DesktopUpdateReleaseForm
          release={selectedRelease}
          onCancel={() => {
            setSelectedRelease(null);
            setErrorMessage(null);
          }}
          onSuccess={async (message) => {
            setSelectedRelease(null);
            setErrorMessage(null);
            setSuccessMessage(message);
            await refresh();
          }}
          onError={(message) => {
            setSuccessMessage(null);
            setErrorMessage(message);
          }}
        />
        </div>
      </SectionSurface>

      <SectionSurface>
        <SectionHeader title={`发布记录 (${data?.releases.length ?? 0})`} description="历史记录保留启用、停用和编辑入口，便于回滚前核对。" />
        <Table className="rounded-none border-0">
          <THead>
            <TR>
              <TH>版本</TH>
              <TH>平台</TH>
              <TH>状态</TH>
              <TH>发布时间</TH>
              <TH>下载地址</TH>
              <TH>操作</TH>
            </TR>
          </THead>
          <TBody>
            {data && data.releases.length > 0 ? (
              data.releases.map((release) => (
                <TR key={release.id}>
                  <TD>
                    <div className="font-mono text-sm text-text-primary">{release.version}</div>
                    <div className="font-mono text-xs text-text-muted">{release.id}</div>
                  </TD>
                  <TD>
                    <div className="font-mono text-xs text-text-primary">{release.platformKey}</div>
                    <div className="text-xs text-text-muted">
                      {release.channel} / {release.target} / {release.arch}
                    </div>
                  </TD>
                  <TD>
                    <Badge tone={release.enabled ? "success" : "warning"}>
                      {release.enabled ? "启用" : "停用"}
                    </Badge>
                  </TD>
                  <TD>
                    <div className="text-xs text-text-secondary">{formatDateTime(release.pubDate ?? release.createdAt)}</div>
                    <div className="text-xs text-text-muted">更新 {formatDateTime(release.updatedAt)}</div>
                  </TD>
                  <TD>
                    <div className="max-w-xs truncate text-xs text-text-secondary" title={release.url}>
                      {release.url}
                    </div>
                    {release.minCurrentVersion ? (
                      <div className="font-mono text-xs text-text-muted">
                        最低版本 {release.minCurrentVersion}
                      </div>
                    ) : null}
                  </TD>
                  <TD>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSelectedRelease(release);
                          setSuccessMessage(null);
                          setErrorMessage(null);
                        }}
                      >
                        编辑
                      </Button>
                      <ToggleReleaseButton
                        release={release}
                        onSuccess={async (message) => {
                          setSuccessMessage(message);
                          setErrorMessage(null);
                          await refresh();
                        }}
                        onError={(message) => {
                          setSuccessMessage(null);
                          setErrorMessage(message);
                        }}
                      />
                    </div>
                  </TD>
                </TR>
              ))
            ) : (
              <EmptyRow colSpan={6} label="暂无桌面更新发布记录。" />
            )}
          </TBody>
        </Table>
      </SectionSurface>
    </div>
  );
}

function DesktopUpdateReleaseForm({
  release,
  onCancel,
  onSuccess,
  onError
}: {
  release: AdminDesktopUpdateReleaseView | null;
  onCancel: () => void;
  onSuccess: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [publishMode, setPublishMode] = useState<PublishMode>("upload");
  const [artifactFile, setArtifactFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm<ReleaseFormValues>({
    resolver: zodResolver(
      release || publishMode === "external"
        ? adminDesktopUpdateReleaseSchema
        : adminDesktopUpdateReleaseUploadSchema
    ),
    values: release ? toFormValues(release) : defaultFormValues()
  });

  const publishMutation = useMutation({
    mutationFn: (input: AdminDesktopUpdateReleaseInput) => adminPublishDesktopUpdateRelease(input),
    onSuccess: async () => {
      reset(defaultFormValues());
      await onSuccess("桌面更新已发布");
    },
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "发布失败")
  });

  const uploadMutation = useMutation({
    mutationFn: (input: {
      metadata: AdminDesktopUpdateReleaseUploadInput;
      artifact: File;
      signature: File;
    }) => adminUploadDesktopUpdateRelease(input),
    onSuccess: async () => {
      reset(defaultFormValues());
      setArtifactFile(null);
      setSignatureFile(null);
      setFileError(null);
      await onSuccess("桌面更新已上传并发布");
    },
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "上传发布失败")
  });

  const updateMutation = useMutation({
    mutationFn: (input: AdminDesktopUpdateReleaseInput) =>
      release
        ? adminUpdateDesktopUpdateRelease(release.id, input)
        : Promise.reject(new Error("missing release")),
    onSuccess: async () => {
      reset(defaultFormValues());
      await onSuccess("桌面更新已保存");
    },
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "保存失败")
  });

  const busy = publishMutation.isPending || uploadMutation.isPending || updateMutation.isPending;
  const target = watch("target");
  const arch = watch("arch");
  const channel = watch("channel");
  const version = watch("version");
  const inferredVersion = artifactFile ? inferDesktopUpdateVersionFromFileName(artifactFile.name) : null;
  const targetOptions = optionsWithCurrent(UPDATE_TARGET_OPTIONS, target || release?.target, "当前系统");
  const archOptions = optionsWithCurrent(UPDATE_ARCH_OPTIONS, arch || release?.arch, "当前架构");
  const channelOptions = optionsWithCurrent(UPDATE_CHANNEL_OPTIONS, channel || release?.channel, "当前通道");
  const displayPlatformKey =
    release && target === release.target && arch === release.arch
      ? release.platformKey
      : platformKeyFromParts(target || "windows", arch || "x86_64");

  return (
    <form
      className="space-y-4"
      onSubmit={handleSubmit((values) => {
        if (release) {
          const input = normalizeReleaseInput(values, release);
          updateMutation.mutate(input);
        } else if (publishMode === "upload") {
          if (!artifactFile || !signatureFile) {
            setFileError("请选择更新安装包和签名文件");
            return;
          }
          const uploadVersion = values.version.trim() || inferredVersion;
          if (!uploadVersion) {
            setFileError("无法从安装包文件名识别版本，请手动填写版本号");
            return;
          }
          setFileError(null);
          uploadMutation.mutate({
            metadata: normalizeReleaseUploadInput(values, uploadVersion),
            artifact: artifactFile,
            signature: signatureFile
          });
        } else {
          const input = normalizeReleaseInput(values, null);
          publishMutation.mutate(input);
        }
      })}
      noValidate
    >
      {!release ? (
        <div className="inline-flex rounded border border-surface-border bg-surface-panel p-1 text-sm">
          <button
            type="button"
            className={`rounded px-3 py-1.5 ${
              publishMode === "upload"
                ? "bg-accent text-white"
                : "text-text-secondary hover:text-text-primary"
            }`}
            onClick={() => {
              setPublishMode("upload");
              setFileError(null);
            }}
          >
            上传发布
          </button>
          <button
            type="button"
            className={`rounded px-3 py-1.5 ${
              publishMode === "external"
                ? "bg-accent text-white"
                : "text-text-secondary hover:text-text-primary"
            }`}
            onClick={() => {
              setPublishMode("external");
              setFileError(null);
            }}
          >
            外链发布
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        <Field
          label="版本"
          htmlFor="version"
          required={Boolean(release) || publishMode === "external" || !inferredVersion}
          error={errors.version?.message}
          hint={
            !release && publishMode === "upload"
              ? inferredVersion
                ? `已从安装包文件名识别：${inferredVersion}，可手动覆盖`
                : artifactFile
                  ? "未能从安装包文件名识别版本，请手动填写"
                  : "选择安装包后会自动识别版本"
              : undefined
          }
        >
          <Input id="version" placeholder="0.2.0" invalid={!!errors.version} {...register("version")} />
        </Field>
        <Field label="系统" htmlFor="target" required error={errors.target?.message}>
          <select
            id="target"
            className="h-9 rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:border-accent/70 focus:outline-none focus:ring-2 focus:ring-accent/40"
            {...register("target")}
          >
            {targetOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="架构" htmlFor="arch" required error={errors.arch?.message}>
          <select
            id="arch"
            className="h-9 rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:border-accent/70 focus:outline-none focus:ring-2 focus:ring-accent/40"
            {...register("arch")}
          >
            {archOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="更新通道" htmlFor="channel" required error={errors.channel?.message}>
          <select
            id="channel"
            className="h-9 rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:border-accent/70 focus:outline-none focus:ring-2 focus:ring-accent/40"
            {...register("channel")}
          >
            {channelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="启用" htmlFor="enabled" error={errors.enabled?.message}>
          <label className="inline-flex h-9 items-center gap-2 rounded border border-surface-border bg-surface-panel px-2.5 text-sm">
            <input id="enabled" type="checkbox" className="h-4 w-4 accent-accent" {...register("enabled")} />
            <span>可拉取</span>
          </label>
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field
          label="平台键"
          htmlFor="platformKey"
          error={errors.platformKey?.message}
          hint="根据系统和架构自动生成，通常不需要修改"
        >
          <Input id="platformKey" readOnly value={displayPlatformKey} />
        </Field>
        <Field label="发布时间" htmlFor="pubDate" error={errors.pubDate?.message}>
          <Input id="pubDate" type="datetime-local" invalid={!!errors.pubDate} {...register("pubDate")} />
        </Field>
        <Field
          label="最低当前版本"
          htmlFor="minCurrentVersion"
          error={errors.minCurrentVersion?.message}
        >
          <Input id="minCurrentVersion" placeholder="0.1.0" {...register("minCurrentVersion")} />
        </Field>
      </div>

      {!release && publishMode === "upload" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="更新安装包"
            htmlFor="artifact"
            required
            error={fileError && !artifactFile ? fileError : undefined}
            hint="选择桌面端打包生成的安装包或更新产物"
          >
            <Input
              id="artifact"
              type="file"
              invalid={!!fileError && !artifactFile}
              onChange={(event) => {
                const nextFile = event.target.files?.[0] ?? null;
                setArtifactFile(nextFile);
                const nextVersion = nextFile ? inferDesktopUpdateVersionFromFileName(nextFile.name) : null;
                if (nextVersion && (!version.trim() || version === inferredVersion)) {
                  setValue("version", nextVersion, { shouldDirty: true, shouldValidate: true });
                }
                setFileError(null);
              }}
            />
          </Field>
          <Field
            label="签名文件"
            htmlFor="signatureFile"
            required
            error={fileError && !signatureFile ? fileError : undefined}
            hint="选择打包生成的 .sig 签名文件"
          >
            <Input
              id="signatureFile"
              type="file"
              accept=".sig,text/plain"
              invalid={!!fileError && !signatureFile}
              onChange={(event) => {
                setSignatureFile(event.target.files?.[0] ?? null);
                setFileError(null);
              }}
            />
          </Field>
        </div>
      ) : (
        <>
          <Field label="下载地址" htmlFor="url" required error={errors.url?.message}>
            <Input id="url" type="url" invalid={!!errors.url} {...register("url")} />
          </Field>

          <Field label="签名" htmlFor="signature" required error={errors.signature?.message}>
            <textarea
              id="signature"
              className="min-h-[96px] w-full rounded border border-surface-border bg-surface-panel px-2.5 py-2 font-mono text-xs text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
              {...register("signature")}
            />
          </Field>
        </>
      )}

      <Field label="发布说明" htmlFor="notes" error={errors.notes?.message}>
        <textarea
          id="notes"
          className="min-h-[96px] w-full rounded border border-surface-border bg-surface-panel px-2.5 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
          {...register("notes")}
        />
      </Field>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={busy || (release ? !isDirty : false)}>
          {release ? <Save className="h-3.5 w-3.5" /> : <UploadCloud className="h-3.5 w-3.5" />}
          {busy ? "提交中…" : release ? "保存发布记录" : publishMode === "upload" ? "上传并发布" : "发布更新"}
        </Button>
        {release ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              reset(defaultFormValues());
              onCancel();
            }}
          >
            取消编辑
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function ToggleReleaseButton({
  release,
  onSuccess,
  onError
}: {
  release: AdminDesktopUpdateReleaseView;
  onSuccess: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: () =>
      release.enabled
        ? adminDisableDesktopUpdateRelease(release.id)
        : adminEnableDesktopUpdateRelease(release.id),
    onSuccess: async () => {
      await onSuccess(release.enabled ? "桌面更新已停用" : "桌面更新已启用");
    },
    onError: (err) => onError(err instanceof CloudApiError ? describeError(err.code) : "操作失败")
  });

  return (
    <Button
      size="sm"
      variant={release.enabled ? "ghost" : "secondary"}
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {release.enabled ? <PauseCircle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
      {mutation.isPending ? "处理中..." : release.enabled ? "停用" : "启用"}
    </Button>
  );
}

function defaultFormValues(): ReleaseFormValues {
  return {
    version: "",
    target: "windows",
    arch: "x86_64",
    platformKey: "",
    channel: "stable",
    url: "",
    signature: "",
    notes: "",
    pubDate: datetimeLocalValue(new Date()),
    minCurrentVersion: "",
    enabled: true
  };
}

function toFormValues(release: AdminDesktopUpdateReleaseView): ReleaseFormValues {
  return {
    version: release.version,
    target: release.target,
    arch: release.arch,
    platformKey: release.platformKey,
    channel: release.channel,
    url: release.url,
    signature: release.signature,
    notes: release.notes ?? "",
    pubDate: release.pubDate ? datetimeLocalValue(new Date(release.pubDate)) : "",
    minCurrentVersion: release.minCurrentVersion ?? "",
    enabled: release.enabled
  };
}

function normalizeReleaseInput(
  values: ReleaseFormValues,
  release: AdminDesktopUpdateReleaseView | null
): AdminDesktopUpdateReleaseInput {
  const target = values.target.trim();
  const arch = values.arch.trim();
  return {
    ...values,
    version: values.version.trim(),
    target,
    arch,
    platformKey:
      release && target === release.target && arch === release.arch
        ? release.platformKey
        : platformKeyFromParts(target, arch),
    channel: values.channel.trim() || "stable",
    url: values.url.trim(),
    signature: values.signature.trim(),
    notes: values.notes?.trim() || undefined,
    pubDate: values.pubDate ? new Date(values.pubDate).toISOString() : undefined,
    minCurrentVersion: values.minCurrentVersion?.trim() || undefined,
    enabled: values.enabled
  };
}

function normalizeReleaseUploadInput(
  values: ReleaseFormValues,
  inferredVersion?: string | null
): AdminDesktopUpdateReleaseUploadInput {
  return {
    version: values.version.trim() || inferredVersion || undefined,
    target: values.target.trim(),
    arch: values.arch.trim(),
    platformKey: platformKeyFromParts(values.target.trim(), values.arch.trim()),
    channel: values.channel.trim() || "stable",
    notes: values.notes?.trim() || undefined,
    pubDate: values.pubDate ? new Date(values.pubDate).toISOString() : undefined,
    minCurrentVersion: values.minCurrentVersion?.trim() || undefined,
    enabled: values.enabled
  };
}

function datetimeLocalValue(date: Date): string {
  return date.toISOString().slice(0, 16);
}

function platformKeyFromParts(target: string, arch: string): string {
  return `${target}-${arch}-nsis`;
}

function optionsWithCurrent(
  options: SelectOption[],
  currentValue: string | undefined,
  fallbackLabel: string
): SelectOption[] {
  if (!currentValue || options.some((option) => option.value === currentValue)) return options;
  return [{ value: currentValue, label: `${fallbackLabel}：${currentValue}` }, ...options];
}
